import React from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus, Star, ScrollText, Trash2 } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import DataTable from '@/components/shared/DataTable';
import SearchableSelect from '@/components/shared/SearchableSelect';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { accountingApi, type LookupBankAccount } from '@/lib/api/accounting';

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
/** حداکثر رقم صحیح موجودی (هم‌خوان با DECIMAL(22,2) در دیتابیس) */
const MAX_BALANCE_INTEGER_DIGITS = 20;

function normalizeFaArDigitsToAscii(value: string): string {
  let out = '';
  for (const ch of value) {
    const p = PERSIAN_DIGITS.indexOf(ch);
    if (p !== -1) {
      out += String(p);
      continue;
    }
    const a = ARABIC_INDIC_DIGITS.indexOf(ch);
    if (a !== -1) {
      out += String(a);
      continue;
    }
    out += ch;
  }
  return out;
}

function formatAccountNumberInput(value: string): string {
  return normalizeFaArDigitsToAscii(value).replace(/\D/g, '').slice(0, 32);
}

function parseBalanceDigits(raw: string): string {
  return normalizeFaArDigitsToAscii(raw).replace(/,/g, '').replace(/\D/g, '');
}

function balancePayloadFromDigits(digits: string): number | string {
  if (!digits || digits === '0') return 0;
  const bi = BigInt(digits);
  const maxSafe = BigInt(Number.MAX_SAFE_INTEGER);
  if (bi <= maxSafe) return Number(digits);
  return digits;
}

/** بخش صحیح موجودی ذخیره‌شده (بدون از دست دادن رقم برای مقدارهای بزرگ وقتی API رشته می‌دهد) */
function integerDigitsFromStoredBalance(raw: number | string | null | undefined): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'string') {
    const head = raw.split('.')[0] ?? '';
    return normalizeFaArDigitsToAscii(head).replace(/\D/g, '');
  }
  if (typeof raw === 'number' && Number.isFinite(raw) && Math.abs(raw) <= Number.MAX_SAFE_INTEGER) {
    return String(Math.trunc(raw));
  }
  return '';
}

function formatBalanceDisplay(raw: number | string | null | undefined): string {
  const digits = integerDigitsFromStoredBalance(raw);
  const n = digits || '0';
  return n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatCardNumber(value: string): string {
  const digits = normalizeFaArDigitsToAscii(value).replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function formatSheba(value: string): string {
  let raw = normalizeFaArDigitsToAscii(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!raw.startsWith('IR')) {
    raw = `IR${raw.replace(/^IR/, '')}`;
  }
  const compact = `IR${raw.slice(2).replace(/\D/g, '').slice(0, 24)}`;
  const body = compact.slice(2).replace(/(.{4})/g, '$1 ').trim();
  return body ? `${compact.slice(0, 2)} ${body}` : compact.slice(0, 2);
}

function normalizeShebaForSubmit(value: string): string {
  const compact = normalizeFaArDigitsToAscii(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (compact.startsWith('IR')) return compact.slice(0, 26);
  return `IR${compact.replace(/^IR/, '').slice(0, 24)}`;
}

function formatMoneyInput(value: string): string {
  const digits = parseBalanceDigits(value);
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** ISO 13616 MOD-97-10 (همان الگوریتم شبا/IBAN) */
function ibanMod97(iban: string): number {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let expanded = '';
  for (const ch of rearranged) {
    if (ch >= '0' && ch <= '9') expanded += ch;
    else if (ch >= 'A' && ch <= 'Z') expanded += String(ch.charCodeAt(0) - 55);
    else if (ch >= 'a' && ch <= 'z') expanded += String(ch.charCodeAt(0) - 87);
  }
  let remainder = 0;
  for (let i = 0; i < expanded.length; i++) {
    remainder = (remainder * 10 + Number(expanded[i])) % 97;
  }
  return remainder;
}

function isValidIranSheba(sheba: string): boolean {
  const normalized = normalizeShebaForSubmit(sheba);
  if (!/^IR\d{24}$/i.test(normalized)) return false;
  return ibanMod97(normalized.toUpperCase()) === 1;
}

const BankAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<LookupBankAccount[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [name, setName] = React.useState('');
  const [bankName, setBankName] = React.useState('');
  const [accountHolderName, setAccountHolderName] = React.useState('');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [cardNumber, setCardNumber] = React.useState('');
  const [shebaNumber, setShebaNumber] = React.useState('');
  const [currentBalance, setCurrentBalance] = React.useState('');
  const [accountType, setAccountType] = React.useState<'company' | 'personal' | 'project'>('company');
  const [submitting, setSubmitting] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [formError, setFormError] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = React.useState<LookupBankAccount | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editId, setEditId] = React.useState<number | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editBankName, setEditBankName] = React.useState('');
  const [editAccountHolderName, setEditAccountHolderName] = React.useState('');
  const [editAccountNumber, setEditAccountNumber] = React.useState('');
  const [editCardNumber, setEditCardNumber] = React.useState('');
  const [editShebaNumber, setEditShebaNumber] = React.useState('');
  const [editAccountType, setEditAccountType] = React.useState<'company' | 'personal' | 'project'>('company');
  const [editSubmitting, setEditSubmitting] = React.useState(false);
  const [editFormError, setEditFormError] = React.useState('');
  const [editFieldErrors, setEditFieldErrors] = React.useState<Record<string, string>>({});
  const [deleting, setDeleting] = React.useState(false);
  const [messageModal, setMessageModal] = React.useState<{ title: string; description: string } | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    accountingApi
      .bankAccounts()
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'عنوان حساب الزامی است.';
    if (!bankName.trim()) errors.bankName = 'نام بانک الزامی است.';
    if (!accountHolderName.trim()) errors.accountHolderName = 'نام صاحب حساب الزامی است.';

    const cardDigits = cardNumber.replace(/\D/g, '');
    if (cardDigits && cardDigits.length !== 16) {
      errors.cardNumber = 'شماره کارت باید دقیقاً ۱۶ رقم باشد.';
    }

    const shebaNormalized = shebaNumber.trim() ? normalizeShebaForSubmit(shebaNumber) : '';
    if (shebaNormalized && !/^IR\d{24}$/i.test(shebaNormalized)) {
      errors.shebaNumber = 'فرمت شبا باید به‌صورت IR + 24 رقم باشد.';
    } else if (shebaNormalized && !isValidIranSheba(shebaNormalized)) {
      errors.shebaNumber = 'شماره شبا معتبر نیست.';
    }
    const balanceDigits = parseBalanceDigits(currentBalance);
    if (!currentBalance.trim()) {
      errors.currentBalance = 'موجودی اولیه الزامی است.';
    } else if (!balanceDigits) {
      errors.currentBalance = 'موجودی اولیه معتبر نیست.';
    } else if (balanceDigits.length > MAX_BALANCE_INTEGER_DIGITS) {
      errors.currentBalance = `موجودی نباید بیش از ${MAX_BALANCE_INTEGER_DIGITS} رقم صحیح باشد.`;
    }

    return errors;
  };

  const validateEditForm = () => {
    const errors: Record<string, string> = {};
    if (!editName.trim()) errors.editName = 'عنوان حساب الزامی است.';
    if (!editBankName.trim()) errors.editBankName = 'نام بانک الزامی است.';
    if (!editAccountHolderName.trim()) errors.editAccountHolderName = 'نام صاحب حساب الزامی است.';

    const cardDigits = editCardNumber.replace(/\D/g, '');
    if (cardDigits && cardDigits.length !== 16) {
      errors.editCardNumber = 'شماره کارت باید دقیقاً ۱۶ رقم باشد.';
    }

    const shebaNormalized = editShebaNumber.trim() ? normalizeShebaForSubmit(editShebaNumber) : '';
    if (shebaNormalized && !/^IR\d{24}$/i.test(shebaNormalized)) {
      errors.editShebaNumber = 'فرمت شبا باید به‌صورت IR + 24 رقم باشد.';
    } else if (shebaNormalized && !isValidIranSheba(shebaNormalized)) {
      errors.editShebaNumber = 'شماره شبا معتبر نیست.';
    }

    return errors;
  };

  const openEditModal = (row: LookupBankAccount) => {
    setEditId(row.id);
    setEditName(row.name || '');
    setEditBankName(row.bank_name || '');
    setEditAccountHolderName(row.account_holder_name || '');
    setEditAccountNumber(formatAccountNumberInput(String(row.account_number ?? '')));
    setEditCardNumber(formatCardNumber(row.card_number || ''));
    setEditShebaNumber(formatSheba(row.sheba_number || ''));
    setEditAccountType(row.account_type);
    setEditFormError('');
    setEditFieldErrors({});
    setEditOpen(true);
  };

  const closeEditModal = () => {
    setEditOpen(false);
    setEditId(null);
    setEditFieldErrors({});
    setEditFormError('');
  };

  const resetAddForm = () => {
    setName('');
    setBankName('');
    setAccountHolderName('');
    setAccountNumber('');
    setCardNumber('');
    setShebaNumber('');
    setCurrentBalance('');
    setAccountType('company');
    setFieldErrors({});
  };

  const openCreateModal = () => {
    resetAddForm();
    setFormError('');
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setFormError('');
    setFieldErrors({});
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setFormError('');
    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('لطفاً خطاهای فرم را اصلاح کنید.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        bank_name: bankName.trim(),
        account_holder_name: accountHolderName.trim(),
        account_number: formatAccountNumberInput(accountNumber) || undefined,
        card_number: cardNumber.trim().replace(/\s+/g, '') || undefined,
        sheba_number: shebaNumber.trim() ? normalizeShebaForSubmit(shebaNumber) : undefined,
        current_balance: balancePayloadFromDigits(parseBalanceDigits(currentBalance)),
        account_type: accountType,
        is_active: true,
      };

      await accountingApi.createBankAccount(payload);
      setSuccessMessage('حساب بانکی با موفقیت اضافه شد.');
      resetAddForm();
      setFieldErrors({});
      closeCreateModal();
      load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'ثبت حساب بانکی ناموفق بود.');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await accountingApi.deleteBankAccount(id);
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'حذف حساب بانکی ناموفق بود.';
      setMessageModal({ title: 'امکان حذف وجود ندارد', description: msg });
    }
  };

  const submitEditModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setEditFormError('');
    const errors = validateEditForm();
    setEditFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setEditFormError('لطفاً خطاهای فرم را اصلاح کنید.');
      return;
    }
    setEditSubmitting(true);
    try {
      const payload = {
        name: editName.trim(),
        bank_name: editBankName.trim(),
        account_holder_name: editAccountHolderName.trim(),
        account_number: formatAccountNumberInput(editAccountNumber) || undefined,
        card_number: editCardNumber.trim().replace(/\s+/g, '') || undefined,
        sheba_number: editShebaNumber.trim() ? normalizeShebaForSubmit(editShebaNumber) : undefined,
        account_type: editAccountType,
        is_active: true,
      };
      await accountingApi.updateBankAccount(editId, payload);
      setSuccessMessage('حساب بانکی با موفقیت ویرایش شد.');
      closeEditModal();
      load();
    } catch (err) {
      setEditFormError(err instanceof Error ? err.message : 'ویرایش حساب بانکی ناموفق بود.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const setDefaultDestination = async (id: number) => {
    try {
      await accountingApi.setDefaultDestination(id);
      setSuccessMessage('حساب مبدا پیش‌فرض تنظیم شد.');
      load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'تنظیم پیش‌فرض ناموفق بود.');
    }
  };

  const columns = React.useMemo<ColumnDef<LookupBankAccount>[]>(
    () => [
      { accessorKey: 'name', header: 'عنوان حساب' },
      { accessorKey: 'bank_name', header: 'بانک' },
      { accessorKey: 'account_holder_name', header: 'صاحب حساب' },
      { accessorKey: 'account_number', header: 'شماره حساب' },
      { accessorKey: 'card_number', header: 'شماره کارت' },
      { accessorKey: 'sheba_number', header: 'شماره شبا' },
      {
        accessorKey: 'current_balance',
        header: 'موجودی',
        cell: ({ row }) => <span className="font-mono">{formatBalanceDisplay(row.original.current_balance)}</span>,
      },
      {
        accessorKey: 'account_type',
        header: 'نوع',
        cell: ({ row }) =>
          row.original.account_type === 'company'
            ? 'شرکت'
            : row.original.account_type === 'project'
              ? 'پروژه'
              : 'شخصی',
      },
      {
        accessorKey: 'is_active',
        header: 'وضعیت',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-1 text-xs ${row.original.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
              {row.original.is_active ? 'فعال' : 'غیرفعال'}
            </span>
            {row.original.is_default_destination && (
              <span className="rounded-full bg-indigo-100 px-2 py-1 text-xs text-indigo-800">مبدا پیش‌فرض</span>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'عملیات',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center justify-end gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-sky-600 hover:bg-sky-50"
              title="ویرایش"
              onClick={() => openEditModal(row.original)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-amber-600 hover:bg-amber-50"
              title="مبدا پیش‌فرض"
              onClick={() => setDefaultDestination(row.original.id)}
            >
              <Star className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-indigo-600 hover:bg-indigo-50"
              title="گردش حساب"
              onClick={() => navigate(`/bank-accounts/${row.original.id}`)}
            >
              <ScrollText className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-rose-600 hover:bg-rose-50"
              title="حذف"
              onClick={() => setDeleteTarget(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="حساب‌های بانکی شرکت"
        description="تعریف و مدیریت حساب‌های بانکی برای انتخاب مبدا/مقصد در ثبت سند. تغییر موجودی از طریق گردش حساب و ثبت سند واریز انجام می‌شود."
        actions={
          <Button type="button" className="gap-2" onClick={openCreateModal}>
            افزودن حساب بانکی
            <Plus className="h-4 w-4 shrink-0" />
          </Button>
        }
      />

      {formError && !createOpen && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{formError}</div>
      )}
      {successMessage && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{successMessage}</div>
      )}

      <LoadingLock loading={loading}>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">لیست حساب‌ها</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={rows} emptyMessage={loading ? 'در حال بارگذاری...' : 'حسابی یافت نشد'} />
        </CardContent>
      </Card>
      </LoadingLock>

      <Dialog open={createOpen} onOpenChange={(v) => !v && closeCreateModal()}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto sm:max-w-2xl [&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>افزودن حساب بانکی</DialogTitle>
            <DialogDescription>اطلاعات حساب جدید را تکمیل و ثبت کنید.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {formError && (
              <div className="sm:col-span-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {formError}
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs text-slate-600">عنوان حساب</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="مثلاً: حساب جاری شرکت" />
              {fieldErrors.name && <p className="mt-1 text-xs text-rose-600">{fieldErrors.name}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">نام بانک</label>
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="نام بانک" />
              {fieldErrors.bankName && <p className="mt-1 text-xs text-rose-600">{fieldErrors.bankName}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">نام صاحب حساب</label>
              <Input value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} placeholder="نام صاحب حساب" />
              {fieldErrors.accountHolderName && <p className="mt-1 text-xs text-rose-600">{fieldErrors.accountHolderName}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">نوع حساب</label>
              <SearchableSelect
                value={accountType}
                onChange={(v) => setAccountType(v as 'company' | 'personal' | 'project')}
                options={[
                  { value: 'company', label: 'شرکت' },
                  { value: 'project', label: 'پروژه' },
                  { value: 'personal', label: 'شخصی' },
                ]}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">شماره حساب</label>
              <Input
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                value={accountNumber}
                onChange={(e) => setAccountNumber(formatAccountNumberInput(e.target.value))}
                placeholder="فقط رقم"
              />
              {fieldErrors.accountNumber && <p className="mt-1 text-xs text-rose-600">{fieldErrors.accountNumber}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">شماره کارت</label>
              <Input
                dir="ltr"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                placeholder="6037 9918 9618 5548"
              />
              {fieldErrors.cardNumber && <p className="mt-1 text-xs text-rose-600">{fieldErrors.cardNumber}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">شماره شبا</label>
              <Input
                dir="ltr"
                value={shebaNumber}
                onChange={(e) => setShebaNumber(formatSheba(e.target.value))}
                placeholder="IR06 2900 0000 1000 3242 0001 01"
              />
              {fieldErrors.shebaNumber && <p className="mt-1 text-xs text-rose-600">{fieldErrors.shebaNumber}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">موجودی اولیه (ریال)</label>
              <Input
                dir="ltr"
                value={currentBalance}
                onChange={(e) => setCurrentBalance(formatMoneyInput(e.target.value))}
                placeholder="0"
              />
              {fieldErrors.currentBalance && <p className="mt-1 text-xs text-rose-600">{fieldErrors.currentBalance}</p>}
            </div>
            <DialogFooter className="sm:col-span-2 sm:justify-end">
              <Button type="button" variant="outline" onClick={closeCreateModal}>
                انصراف
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'در حال ثبت...' : 'ثبت حساب'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(v) => !v && closeEditModal()}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto sm:max-w-2xl [&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>ویرایش حساب بانکی</DialogTitle>
            <DialogDescription className="text-right">
              {editId ? `شناسه حساب: ${editId}. ` : ''}
              موجودی از این فرم ویرایش نمی‌شود؛ برای واریز و افزایش مانده از صفحهٔ «گردش حساب» سند واریز ثبت کنید.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEditModal} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {editFormError && (
              <div className="sm:col-span-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {editFormError}
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs text-slate-600">عنوان حساب</label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="عنوان حساب" />
              {editFieldErrors.editName && <p className="mt-1 text-xs text-rose-600">{editFieldErrors.editName}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">نام بانک</label>
              <Input value={editBankName} onChange={(e) => setEditBankName(e.target.value)} placeholder="نام بانک" />
              {editFieldErrors.editBankName && <p className="mt-1 text-xs text-rose-600">{editFieldErrors.editBankName}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">نام صاحب حساب</label>
              <Input value={editAccountHolderName} onChange={(e) => setEditAccountHolderName(e.target.value)} placeholder="نام صاحب حساب" />
              {editFieldErrors.editAccountHolderName && <p className="mt-1 text-xs text-rose-600">{editFieldErrors.editAccountHolderName}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">نوع حساب</label>
              <SearchableSelect
                value={editAccountType}
                onChange={(v) => setEditAccountType(v as 'company' | 'personal' | 'project')}
                options={[
                  { value: 'company', label: 'شرکت' },
                  { value: 'project', label: 'پروژه' },
                  { value: 'personal', label: 'شخصی' },
                ]}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">شماره حساب</label>
              <Input
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                value={editAccountNumber}
                onChange={(e) => setEditAccountNumber(formatAccountNumberInput(e.target.value))}
                placeholder="فقط رقم"
              />
              {editFieldErrors.editAccountNumber && <p className="mt-1 text-xs text-rose-600">{editFieldErrors.editAccountNumber}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">شماره کارت</label>
              <Input
                dir="ltr"
                value={editCardNumber}
                onChange={(e) => setEditCardNumber(formatCardNumber(e.target.value))}
                placeholder="6037 9918 9618 5548"
              />
              {editFieldErrors.editCardNumber && <p className="mt-1 text-xs text-rose-600">{editFieldErrors.editCardNumber}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">شماره شبا</label>
              <Input
                dir="ltr"
                value={editShebaNumber}
                onChange={(e) => setEditShebaNumber(formatSheba(e.target.value))}
                placeholder="IR06 2900 0000 1000 3242 0001 01"
              />
              {editFieldErrors.editShebaNumber && <p className="mt-1 text-xs text-rose-600">{editFieldErrors.editShebaNumber}</p>}
            </div>
            <DialogFooter className="sm:col-span-2 sm:justify-end">
              <Button type="button" variant="outline" onClick={closeEditModal}>
                انصراف
              </Button>
              <Button type="submit" disabled={editSubmitting}>
                {editSubmitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
        title="حذف حساب بانکی"
        description={
          deleteTarget
            ? `حساب «${deleteTarget.name}» از بانک «${deleteTarget.bank_name}» حذف شود؟`
            : ''
        }
        confirmLabel="بله، حذف شود"
        cancelLabel="انصراف"
        variant="destructive"
        loading={deleting}
        onConfirm={async () => {
          if (!deleteTarget) return;
          setDeleting(true);
          try {
            await remove(deleteTarget.id);
            setDeleteTarget(null);
          } finally {
            setDeleting(false);
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(messageModal)}
        onOpenChange={(v) => {
          if (!v) setMessageModal(null);
        }}
        title={messageModal?.title ?? 'پیام سیستم'}
        description={messageModal?.description ?? ''}
        confirmLabel="متوجه شدم"
        showCancel={false}
        onConfirm={() => setMessageModal(null)}
      />
    </div>
  );
};

export default BankAccountsPage;
